// =============================================================================
// BP-08-05: Vendor Portal Frontend - ReviewCard Component
// =============================================================================

import React, { useState } from 'react';
import { Star, ThumbsUp, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import type { VendorReview } from '@/features/vendor/types/vendorTypes';
import { ReviewResponseForm } from './ReviewResponseForm';

interface ReviewCardProps {
  review: VendorReview;
  onRespond?: (reviewId: string, response: string) => Promise<void>;
  onUpdateResponse?: (reviewId: string, response: string) => Promise<void>;
  onDeleteResponse?: (reviewId: string) => Promise<void>;
}

export function ReviewCard({
  review,
  onRespond,
  onUpdateResponse,
  onDeleteResponse,
}: ReviewCardProps) {
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleRespond = async (response: string) => {
    if (review.vendorResponse) {
      await onUpdateResponse?.(review.id, response);
    } else {
      await onRespond?.(review.id, response);
    }
    setShowResponseForm(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          {review.userAvatar ? (
            <img
              src={review.userAvatar}
              alt={review.userName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-sage-light flex items-center justify-center">
              <span className="text-sm font-semibold text-white">
                {review.userName.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-navy">{review.userName}</span>
              {review.verifiedPurchase && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  Verified Purchase
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {renderStars(review.rating)}
              <span className="text-sm text-warm-gray">
                {formatDate(review.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="flex items-center gap-2 text-sm">
          {review.productImage && (
            <img
              src={review.productImage}
              alt={review.productName}
              className="w-8 h-8 rounded object-cover"
            />
          )}
          <span className="text-warm-gray max-w-[150px] truncate">
            {review.productName}
          </span>
        </div>
      </div>

      {/* Review Content */}
      <div className="mb-4">
        {review.title && (
          <h4 className="font-semibold text-navy mb-2">{review.title}</h4>
        )}
        <p className={`text-navy ${!isExpanded && 'line-clamp-3'}`}>
          {review.content}
        </p>
        {review.content.length > 200 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-terracotta hover:text-terracotta-light mt-1 flex items-center gap-1"
          >
            {isExpanded ? (
              <>
                Show less <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                Read more <ChevronDown className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>

      {/* Pros & Cons */}
      {(review.pros.length > 0 || review.cons.length > 0) && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {review.pros.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-green-700 mb-2">Pros</h5>
              <ul className="space-y-1">
                {review.pros.map((pro, i) => (
                  <li key={i} className="text-sm text-navy flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">+</span>
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {review.cons.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-red-700 mb-2">Cons</h5>
              <ul className="space-y-1">
                {review.cons.map((con, i) => (
                  <li key={i} className="text-sm text-navy flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">−</span>
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Review Images */}
      {review.images.length > 0 && (
        <div className="flex gap-2 mb-4">
          {review.images.map((image, i) => (
            <img
              key={i}
              src={image}
              alt={`Review image ${i + 1}`}
              className="w-16 h-16 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
            />
          ))}
        </div>
      )}

      {/* Helpful Count */}
      <div className="flex items-center gap-4 text-sm text-warm-gray mb-4">
        <span className="flex items-center gap-1">
          <ThumbsUp className="w-4 h-4" />
          {review.helpfulCount} found this helpful
        </span>
      </div>

      {/* Vendor Response */}
      {review.vendorResponse && !showResponseForm && (
        <div className="bg-cream-dark rounded-lg p-4 border-l-4 border-terracotta">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-navy">Your Response</span>
            <span className="text-xs text-warm-gray">
              {review.vendorRespondedAt && formatDate(review.vendorRespondedAt)}
            </span>
          </div>
          <p className="text-sm text-navy">{review.vendorResponse}</p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setShowResponseForm(true)}
              className="text-sm text-terracotta hover:text-terracotta-light font-medium"
            >
              Edit Response
            </button>
            <button
              onClick={() => onDeleteResponse?.(review.id)}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Response Form */}
      {showResponseForm && (
        <ReviewResponseForm
          initialValue={review.vendorResponse || ''}
          onSubmit={handleRespond}
          onCancel={() => setShowResponseForm(false)}
        />
      )}

      {/* Respond Button */}
      {!review.vendorResponse && !showResponseForm && (
        <button
          onClick={() => setShowResponseForm(true)}
          className="flex items-center gap-2 text-sm font-medium text-terracotta hover:text-terracotta-light transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Respond to Review
        </button>
      )}
    </div>
  );
}

export default ReviewCard;

